import assert from 'assert';
import KU_QUERY from '@/constants/common/searchParamKeys';
import {addParamToCustomUrl} from '@/utils/kuQuery';
import {TreeNodeData} from '@/domains/space/Repository/SidebarNew/type';
import routers, {buildPath} from '@/config/routers';
import {InheritRepo, OwnerType, RepositoryScope, Role, SettingTabs, URL_SOURCE} from '@/Constant';
import {PathParams} from '@/interface';
import sdk from '@/sdk';
import * as store from '@/store';
import {BOOK_TYPE} from '@/constants/document/doc';
import {sourceType} from '@/constants/common';
import {getIsBaikeRepo} from '@/pages/baike/utils';
import {isPersonal} from './Utils/fixRepositoryNameWithPersonal';
// import {dateFormat} from '../../utils/dateUtils';

export async function refreshCurRepository(spaceGuid: string, groupGuid: string, repositoryGuid: string) {
    // 获取 or 更新 当前页面的 知识库信息（不适用 列表中查询其他知识库信息）
    const repository = await sdk.as.getRepositoryByGuid(spaceGuid, groupGuid, repositoryGuid);
    store.setCurRepository(repository);
    return repository;
}

export function isPersonalMyself(repository: Repository | null | undefined, userGuid: string) {
    return isPersonal(repository) && repository?.userGuid === userGuid;
}

export function isTeam(repository: Repository | null) {
    return !!repository && !isPersonal(repository);
}

export function isTeamOwner(repository: Repository | null, userGuid: string) {
    return isTeam(repository) && userGuid === repository?.userGuid;
}

// 私密库&&无权限
export function isPrivateAndNoPower() {
    const curRepository = store.getCurRepository();
    const isPrivate = !curRepository?.scope || RepositoryScope.Repository === curRepository?.scope;
    return isPrivate
            && curRepository
            && !curRepository?.roleName
            && !isPersonalMyself(curRepository, sdk.as.user?.userGuid || '');
}
// 受限知识库&&无权限
export function isRestrictedAndNoPower() {
    const curRepository = store.getCurRepository();
    const isRestricted = curRepository?.scope === RepositoryScope.SpaceReaderLimit;
    return isRestricted
            && curRepository
            && (!curRepository?.roleName || curRepository?.roleName === Role.LimitedReader)
            && !isPersonalMyself(curRepository, sdk.as.user?.userGuid || '');
}

export function getRepositoryName(repository: Repository) {
    // 后台接口返回的 OwnerType 类型可能不一致，所以使用了 ==
    // eslint-disable-next-line eqeqeq
    if (!!repository && repository?.ownerType == OwnerType.User) {
        return `${repository.name}的知识库`;
    }
    return repository.name || '';
}

export function getTreeNodeFromDocument(doc: WizDocument) {
    const params: PathParams = {
        spaceGuid: doc.spaceGuid || '',
        groupGuid: doc.groupGuid || '',
        repositoryGuid: doc.repositoryGuid || '',
        docGuid: doc.docGuid,
        name: encodeURIComponent(doc.name || ''),
    };
    // const landingPage = kuPiper.runProxy(kuPiper.proxyNameMap.landingPageLinkAddress, () => {
    //     return buildPath(routers.Knowledge.path, params); // 存在循环引用，解决问题后替换下面代码
    // });
    const isBaikeRoute = store.getData(store.KEYS.IS_BAIKE_ROUTE);
    const isBaikeRepo = store.getData(store.KEYS.IS_BAIKE_REPO);
    let url = buildPath(isBaikeRoute ? routers.baike.entity : routers.Knowledge.path, params);
    function getSpliceUrl(key: any) {
        return addParamToCustomUrl(
            `${window.location.origin}${url}`,
            KU_QUERY.space.source,
            key).path;
    }
    if (isBaikeRepo) {
        url = getSpliceUrl(sourceType[107]);
    } else if (isPrivateAndNoPower()) {
        url = getSpliceUrl(URL_SOURCE.privateNoPowerSideBarDocs);
    } else {
        url = getSpliceUrl(URL_SOURCE.sideBarDocs);
    }
    // const url = landingPage(params);

    const data: TreeNodeData = {
        id: doc.docGuid || '',
        text: doc.name || '',
        url,
        isLeaf: !doc.childCount,
        origin: doc,
        type: doc.type,
    };
    return data;
}

export function curUserIsAdmin(repository: Repository) {
    return repository.roleName === Role.Owner || repository.roleName === Role.Admin;
}

export function curUserIsMember(repository: Repository) {
    return repository.roleName === Role.Member;
}

export function curUserIsReader(repository: Repository) {
    return repository.roleName === Role.Reader;
}

export function curUserCanRead(repository: Repository) {
    return curUserIsAdmin(repository)
        || curUserIsMember(repository)
        || repository.roleName === Role.Reader;
}

export function canSettingKnowledge(repository: Repository) {
    const isAdmin = curUserIsAdmin(repository);
    assert(sdk.as.user);
    const isMyselfKnowledge = isPersonalMyself(repository, sdk.as.user.userGuid);
    return isAdmin || isMyselfKnowledge;
}
export function canEditInKnowledge(
    repository: Repository | null | undefined, bookType?: string | number, inheritRepo?: number) {
    if (!repository) {
        return false;
    }
    const isAdmin = curUserIsAdmin(repository);
    const isMember = curUserIsMember(repository);
    assert(sdk.as.user);
    const isMyselfKnowledge = isPersonalMyself(repository, sdk.as.user.userGuid);
    // 知识本权限缩小后不再支持知识库成员编辑
    if (bookType !== BOOK_TYPE.NO_BOOK && inheritRepo === InheritRepo.Not_Inherit) {
        return isAdmin || isMyselfKnowledge;
    }
    return isAdmin || isMember || isMyselfKnowledge;
}
export function canShowTagKnowledge(repository: Repository | null | undefined) {
    if (!repository) {
        return false;
    }
    const isAdmin = curUserIsAdmin(repository);
    const isMember = curUserIsMember(repository);
    return (isAdmin || isMember);
}

// 判断是否是页面管理员
export function getIsDocAdmin(doc: WizDocument) {
    const {bookType, inheritRepo} = doc;
    const isRepoAdmin = doc.repositoryRoleName === Role.Owner || doc.repositoryRoleName === Role.Admin;
    if (bookType !== BOOK_TYPE.NO_BOOK && inheritRepo === InheritRepo.Not_Inherit) {
        return doc.docRoleName === Role.DocAdmin;
    } else {
        return isRepoAdmin || doc.docRoleName === Role.DocAdmin;
    }
}

// 有读权限
export function canReadInKnowledge(repository: Repository | null | undefined) {
    if (!repository) {
        return false;
    }
    assert(sdk.as.user);
    const isMyselfKnowledge = isPersonalMyself(repository, sdk.as.user.userGuid);
    return curUserCanRead(repository) || isMyselfKnowledge;
}

export function getIsOwner(role: string = '') {
    return role === Role.Owner;
}

export function isAdmin(role: string | null) {
    return (role === Role.Owner || role === Role.Admin);
}

export function createSettingUrl(repository: Repository) {
    const params: PathParams = {
        spaceGuid: repository.spaceGuid || '',
        groupGuid: repository.groupGuid || '',
        repositoryGuid: repository.repositoryGuid || '',
        tab: SettingTabs.Security,
    };
    return buildPath(routers.Knowledge.settingPath, params);
}

export function createHomeTagUrl(repository: Repository) {
    const isBaikeRoute = store.getData(store.KEYS.IS_BAIKE_ROUTE); // TODO后期解决闪烁问题移入插槽 @甄棒
    const params: PathParams = {
        spaceGuid: repository.spaceGuid || '',
        groupGuid: repository.groupGuid || '',
        repositoryGuid: repository.repositoryGuid || '',
        tagName: '',
    };
    return buildPath(isBaikeRoute ? routers.baike.homeTagPath : routers.Knowledge.homeTagPath, params);
}

export function createRecycleBinUrl(repository: Repository) {
    const params: PathParams = {
        spaceGuid: repository.spaceGuid || '',
        groupGuid: repository.groupGuid || '',
        repositoryGuid: repository.repositoryGuid || '',
    };
    return buildPath(routers.Knowledge.recycleBinPath, params);
}

export function createHomeUrl(repository: Repository) {
    const isPersonalRepository = isPersonal(repository);
    const isMyself = isPersonalMyself(repository, sdk.as.user?.userGuid || '');
    if (isPersonalRepository) {
        return buildPath(routers.PersonalHome.path, {
            // @todo personalPage 需要后端增加字段
            // @ts-ignore
            userName: repository.userAvatar || (isMyself ? sdk.as.user?.avatar : ''),
        });
    }
    const isBaikeRoute = getIsBaikeRepo(repository.repositoryGuid); // TODO后期解决闪烁问题移入插槽 @甄棒
    const params: PathParams = {
        spaceGuid: repository.spaceGuid || '',
        groupGuid: repository.groupGuid || '',
        repositoryGuid: repository.repositoryGuid || '',
        docGuid: '',
    };
    return buildPath(isBaikeRoute ? routers.baike.index : routers.Knowledge.path, params);
}

export function createMemberManageUrl(repository: Repository) {
    const params: PathParams = {
        spaceGuid: repository.spaceGuid || '',
        groupGuid: repository.groupGuid || '',
        repositoryGuid: repository.repositoryGuid || '',
        docGuid: '',
    };
    return buildPath(routers.Knowledge.membersPath, params);
}

export function getServerNow() {
    const now = Date.now() + 1000 * 60 * 60 * 24 * 365;
    return now;
}

